import type { FC } from "react";
import { useBoolean } from "usehooks-ts";

import { groupAccessIdFromGroupId } from "../../../../shared/access-id";

import { PageGroupMember } from "components/materials/page/groups/PageGroupMember";
import { PreviewUserNames } from "components/materials/page/groups/PreviewUserNames";
import {
  DraggablePageGroupUserType,
  DraggableSectionUserType,
} from "components/materials/page/groups/helpers";
import { Trans, t } from "i18n/i18n";
import { Card } from "mds/components/Card";
import { ConfirmDeletionDialog } from "mds/components/ConfirmDeletionDialog";
import { DragAndDrop } from "mds/components/DragAndDrop";
import { Tooltip } from "mds/components/Tooltip";
import { TooltipTrigger } from "mds/components/TooltipTrigger";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import { CircleInfoIcon, PersonSingleIcon, TrashDeleteIcon } from "mds/icons";
import { useAccessIdHasData } from "sharedb/ShareDBDoc";
import { storeApi } from "store/index";
import {
  pageGroupSelectors,
  pageSelectors,
  selectPreviewUserIdsForPageGroup,
} from "store/selectors";
import {
  selectPageGroupUsersByPageGroupId,
  selectSortedPageGroupUserIdsByPageGroupId,
  selectUserIdsByPageGroupId,
} from "store/selectors/page-groups";
import { useHasItemsSelector, useListSelector, useObjectSelector } from "store/store-hooks";
import { GroupInteractionModeType } from "utils/page-utils";

interface PageGroupEditCardProps {
  pageGroupId: string;
  number: number;
  totalPageGroups: number;
  pageId: string;
  onRemove?: (studentSectionUserId: string) => void;
  moveSectionUserLocally: (
    item: DraggableSectionUserType | DraggablePageGroupUserType,
    toIndex: number,
    dropContainerId: string,
  ) => void;
  onDropSectionUser: (
    droppedItem: DraggableSectionUserType | DraggablePageGroupUserType,
    toIndex: number,
    sourceContainerId: string,
  ) => void;
  showDeleteButton: boolean;
  hasAssessedSubmissions: boolean;
  isReadOnly?: boolean;
  showEmptyGroupTip?: boolean;
}

export const PageGroupEditCard: FC<PageGroupEditCardProps> = ({
  pageGroupId,
  number,
  totalPageGroups,
  pageId,
  onRemove,
  moveSectionUserLocally,
  onDropSectionUser,
  showDeleteButton,
  hasAssessedSubmissions,
  isReadOnly = false,
  showEmptyGroupTip = true,
}) => {
  const page = useObjectSelector(
    (s) => pageSelectors.selectById(s, pageId),
    ["worksheet_id", "interaction_mode", "max_group_size"],
  );
  const pageGroup = useObjectSelector(
    (s) => pageGroupSelectors.selectById(s, pageGroupId),
    ["id", "auto_created", "page_id"],
  );
  const pageGroupUserIds = useListSelector((s) =>
    selectSortedPageGroupUserIdsByPageGroupId(s, pageGroupId),
  );
  const memberUserIds = useListSelector((s) => selectUserIdsByPageGroupId(s, pageGroupId));
  const hasPageGroupUsers = useHasItemsSelector((s) =>
    selectPageGroupUsersByPageGroupId(s, pageGroupId),
  );
  const previewUserIdsForGroup = useListSelector((state) =>
    selectPreviewUserIdsForPageGroup(state, pageId, pageGroupId),
  );
  const groupAccessId = groupAccessIdFromGroupId(pageGroupId);
  const hasData = useAccessIdHasData(page?.worksheet_id, groupAccessId);
  const totalMemberCount = pageGroupUserIds.length;
  const maxGroupSize = page?.max_group_size ?? null;
  const memberUserIdSet = new Set(memberUserIds);
  const previewUserIds = previewUserIdsForGroup.filter((userId) => !memberUserIdSet.has(userId));

  const hasNoStudents = !hasPageGroupUsers;
  const isDeleteGroupEnabled = totalPageGroups > 2 && (hasNoStudents || !hasAssessedSubmissions);

  const {
    value: confirmationDialogIsOpen,
    setTrue: openConfirmationDialog,
    setFalse: closeConfirmationDialog,
  } = useBoolean(false);

  const confirmDeleteGroup = () => {
    if (hasData) {
      openConfirmationDialog();
    } else {
      storeApi.page_groups.destroy(pageGroupId);
    }
  };

  if (!page || !pageGroup) {
    return null;
  }

  const showRoster = totalMemberCount > 0 || previewUserIds.length > 0;

  return (
    <>
      <DragAndDrop
        className="w-full"
        dragItemKind="section_user"
        dropContainerId={pageGroupId}
        droppable={!isReadOnly}
        onDraggedItemHover={moveSectionUserLocally}
      >
        <Card
          className="flex w-full flex-col flex-wrap p-3"
          data-testid={`group-card-${pageGroupId}`}
          kind="outlined"
        >
          <div className="@max-[460px]/group-config:flex-wrap @max-[460px]/group-config:gap-y-1 mb-1 flex min-h-8 w-full items-center justify-between">
            <div className="body-m @max-[460px]/group-config:max-w-full @max-[460px]/group-config:shrink-0 @max-[460px]/group-config:whitespace-normal @max-[460px]/group-config:[overflow-wrap:anywhere] ml-2 flex items-center gap-1 truncate text-blue">
              {t("common.group_title", { number })}

              {pageGroup.auto_created && (
                <Tooltip content={t("tooltip.auto_created_group")}>
                  <CircleInfoIcon className="text-orange" size="s" />
                </Tooltip>
              )}
            </div>

            <div className="flex gap-1">
              {maxGroupSize !== null ? (
                <TooltipTrigger
                  content={t("group_configuration_card.tooltip.spots_filled", {
                    count: maxGroupSize,
                    filled: totalMemberCount,
                  })}
                >
                  <div className="body-xs flex items-center justify-center gap-1 px-2 text-black-tint-40">
                    <PersonSingleIcon />
                    {totalMemberCount}/{maxGroupSize}
                  </div>
                </TooltipTrigger>
              ) : (
                totalMemberCount > 0 && (
                  <div className="body-xs flex items-center justify-center gap-1 px-2 text-black-tint-40">
                    <PersonSingleIcon />
                    {totalMemberCount}
                  </div>
                )
              )}

              {showDeleteButton && (
                <SquareIconButton
                  aria-disabled={!isDeleteGroupEnabled}
                  aria-label={
                    hasAssessedSubmissions
                      ? t("group_configuration_card.tooltip.cannot_delete_assessed")
                      : t("group_configuration_card.tooltip.delete_group")
                  }
                  eventName="Group settings - Delete group"
                  kind="tertiary"
                  size="s"
                  onClick={confirmDeleteGroup}
                >
                  <TrashDeleteIcon />
                </SquareIconButton>
              )}
            </div>
          </div>
          {!showRoster && showEmptyGroupTip ? (
            <div className="body-s ml-2 mt-1 text-black-tint-55">
              <Trans
                components={{
                  distribute: <strong className="font-semibold text-black-tint-20" />,
                }}
                i18nKey={`group_configuration_card.empty_group_tip.${
                  page.interaction_mode as GroupInteractionModeType
                }`}
              />
            </div>
          ) : (
            showRoster && (
              <>
                {pageGroupUserIds.map((pageGroupUserId, index) => (
                  <PageGroupMember
                    groupPageId={pageGroup.page_id}
                    isReadOnly={isReadOnly}
                    key={pageGroupUserId}
                    order={index}
                    pageGroupId={pageGroupId}
                    pageGroupUserId={pageGroupUserId}
                    pageId={pageId}
                    onDropSectionUser={onDropSectionUser}
                    onRemove={onRemove}
                  />
                ))}
                <PreviewUserNames userIds={previewUserIds} variant="list" />
              </>
            )
          )}
        </Card>
      </DragAndDrop>

      <ConfirmDeletionDialog
        glossaryKey="group"
        instanceName={t("common.group_title", { number })}
        open={confirmationDialogIsOpen}
        onClose={closeConfirmationDialog}
        onDelete={() => storeApi.page_groups.destroy(pageGroupId)}
      >
        <div className="mt-4 border-l-2 border-red pl-4 font-bold">
          {t("confirm_delete_modal.student_content_warning", {
            name: t("common.group_title", { number }),
          })}
        </div>
      </ConfirmDeletionDialog>
    </>
  );
};
